import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import debounce from 'lodash/debounce';

import {
  COMPONENT_LAYOUT_HORIZONTAL,
  LANGUAGE_CODE_EN_AU,
} from 'global/constants';
import {
  EditProps,
  FormDataEdit as FormData,
  FIRST_OPERAND_FIELD,
  SECOND_OPERAND_FIELD,
  EquationResponseProperties,
  ERROR_ELEMENT_ID,
} from './equation-response.constants';
import * as Styled from './equation-response-edit.style';
import { getOperatorSymbol, isOperandValid } from './equation-response.utils';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useUpdateComponent } from 'approot/activity-builder/activity-builder.hooks';
import { EquationResponseHr } from './equation-response.style';
import { spacing16 } from 'approot/shared/styles/spacing.style';
import { SubActivityEdit } from '../sub-activity/sub-activity-edit';
import { removeTypenameFromObject } from 'approot/shared/logic/remove-typename';

const EquationResponseEdit = ({
  id,
  sectionId,
  contentId,
  properties,
  type,
  sort,
  subActivityProperties,
  version,
}: EditProps) => {
  const { showError } = useSnackbar();

  const {
    register,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    mode: 'onChange',
  });

  const hasErrors =
    errors?.[FIRST_OPERAND_FIELD] || errors?.[SECOND_OPERAND_FIELD];

  const { updateComponent } = useUpdateComponent({
    onError: () =>
      showError('There was an error updating this equation response'),
  });

  const handleOnChange = useCallback(
    (newProperties: Partial<EquationResponseProperties>) => {
      if (hasErrors) return;

      const { __typename, layout, ...restProperties } = properties;
      updateComponent({
        sectionId,
        componentId: id,
        componentData: {
          type,
          properties: {
            ...restProperties,
            layout: layout ? removeTypenameFromObject(layout) : null,
            ...newProperties,
          },
          sort,
        },
      });
    },
    [hasErrors, id, properties, sectionId, sort, type, updateComponent]
  );

  const debounceOnChange = debounce(handleOnChange, 300);

  const isHorizontal =
    properties.layout?.orientation === COMPONENT_LAYOUT_HORIZONTAL;

  const firstOperandLabel = 'Enter your first operand';
  const secondOperandLabel = 'Enter your second operand';

  useEffect(() => {
    reset({
      [FIRST_OPERAND_FIELD]: `${properties.firstOperand}`,
      [SECOND_OPERAND_FIELD]: `${properties.secondOperand}`,
    });
    // eslint-disable-next-line
  }, [properties.layout]);

  return (
    <SubActivityEdit
      id={id}
      component={{
        id,
        type,
        sort,
        properties,
        sectionId,
        language: LANGUAGE_CODE_EN_AU,
        version,
        __typename: 'ActivitiesEquationResponseComponent',
      }}
      subActivityDetails={subActivityProperties}
    >
      {() => (
        <>
          <Styled.EquationResponseForm noValidate $vertical={!isHorizontal}>
            {isHorizontal ? (
              <>
                <Styled.EquationResponseInput
                  id={FIRST_OPERAND_FIELD}
                  {...register(FIRST_OPERAND_FIELD, {
                    validate: value =>
                      isOperandValid(value || '') ||
                      'Please enter a valid number',
                  })}
                  onChange={event => {
                    debounceOnChange({
                      firstOperand: Number(event.target.value),
                    });
                    register(FIRST_OPERAND_FIELD).onChange(event);
                  }}
                  aria-label={firstOperandLabel}
                  aria-invalid={!!hasErrors}
                  aria-describedby={ERROR_ELEMENT_ID}
                  $hasError={!!hasErrors}
                />

                <Styled.EquationResponseOperator>
                  {getOperatorSymbol(properties.operation)}
                </Styled.EquationResponseOperator>

                <Styled.EquationResponseInput
                  id={SECOND_OPERAND_FIELD}
                  {...register(SECOND_OPERAND_FIELD, {
                    validate: value =>
                      isOperandValid(value || '') ||
                      'Please enter a valid number',
                  })}
                  onChange={event => {
                    debounceOnChange({
                      secondOperand: Number(event.target.value),
                    });
                    register(SECOND_OPERAND_FIELD).onChange(event);
                  }}
                  aria-label={secondOperandLabel}
                  aria-invalid={!!hasErrors}
                  aria-describedby={ERROR_ELEMENT_ID}
                  $hasError={!!hasErrors}
                />

                <Styled.EquationResponseOperator>
                  =
                </Styled.EquationResponseOperator>

                <Styled.EquationResponseAnswer>?</Styled.EquationResponseAnswer>
              </>
            ) : (
              <>
                <Styled.EquationResponseInputVertical
                  id={FIRST_OPERAND_FIELD}
                  {...register(FIRST_OPERAND_FIELD, {
                    validate: value =>
                      isOperandValid(value || '') ||
                      'Please enter a valid number',
                  })}
                  onChange={event => {
                    debounceOnChange({
                      firstOperand: Number(event.target.value),
                    });
                    register(FIRST_OPERAND_FIELD).onChange(event);
                  }}
                  aria-label={firstOperandLabel}
                  aria-invalid={!!hasErrors}
                  aria-describedby={ERROR_ELEMENT_ID}
                />

                <Styled.EquationResponseOperatorWrapper>
                  <Styled.EquationResponseOperator>
                    {getOperatorSymbol(properties.operation)}
                  </Styled.EquationResponseOperator>

                  <Styled.EquationResponseInputVertical
                    id={SECOND_OPERAND_FIELD}
                    {...register(SECOND_OPERAND_FIELD, {
                      validate: value =>
                        isOperandValid(value || '') ||
                        'Please enter a valid number',
                    })}
                    onChange={event => {
                      debounceOnChange({
                        secondOperand: Number(event.target.value),
                      });
                      register(SECOND_OPERAND_FIELD).onChange(event);
                    }}
                    aria-label={secondOperandLabel}
                    aria-invalid={!!hasErrors}
                    aria-describedby={ERROR_ELEMENT_ID}
                  />
                </Styled.EquationResponseOperatorWrapper>

                <EquationResponseHr />

                <Styled.EquationResponseAnswerVertical>
                  ?
                </Styled.EquationResponseAnswerVertical>
              </>
            )}
          </Styled.EquationResponseForm>

          {hasErrors && (
            <ErrorMessageStyled
              id={ERROR_ELEMENT_ID}
              $marginTop={spacing16}
              $centreText
            >
              Please add whole numbers between 0 and 999.
            </ErrorMessageStyled>
          )}
        </>
      )}
    </SubActivityEdit>
  );
};

export default EquationResponseEdit;
